import classNames from 'classnames';
import { FC, HTMLAttributes, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../context/authContext';
import { getPlan } from '../../../services/apiService';
import { useGeneratePaymentLink } from '../../../services/userService';
import { motion } from 'framer-motion';
import { appPages } from '../../../config/pages.config';
import Icon from '../../../components/icon/Icon';
import SubscriptionModal from './SubscriptionModal';
import { use } from 'i18next';

interface ISubscriptionProductProps extends HTMLAttributes<HTMLDivElement> {
	className?: string;
	title: string;
	description: string;
	showSubscribeButton?: boolean;
	price: number;
	priceId: string;
	duration: string;
}

const SubscriptionProduct: FC<ISubscriptionProductProps> = (props) => {
	const navigate = useNavigate();
	const {
		title,
		description,
		className,
		showSubscribeButton = false,
		price,
		priceId,
		duration,
	} = props;
	const classes = classNames(className);
	const { linkData, error, isLoading, trigger, freeTrialTrigger } = useGeneratePaymentLink();
	const [belowPriceDisabled, setBelowPriceDisabled] = useState(false);
	const [selectedPrice, setSelectedPrice] = useState<any>();
	const [showModal, setShowModal] = useState(false);

	const { userData, planData, onLogout } = useAuth();

	useEffect(() => {
		if (planData?.planPrice === '$' + price) {
			setSelectedPrice(true);
		} else {
			setSelectedPrice(false);
		}

		if (planData?.planPrice) {
			const currentPrice = parseFloat(planData?.planPrice.replace('$', ''));
			if (price < currentPrice) {
				setBelowPriceDisabled(true);
			}
		}
	}, [planData, price]);
	
	const isUpgrade = () => {
		// Check if planData exists and has planPrice property
		if (!planData || !planData.planPrice) return false;
		
		// Get current price without the $ sign, parse as number
		const currentPrice = parseFloat(planData.planPrice.replace('$', ''));
		return price > currentPrice;
	};
	
	const handleSubscribeClick = () => {
		// If it's a free plan, proceed directly
		if (priceId === '1') {
			handleSubscribe();
			return;
		}
		
		// If user already has a plan, check if it's an upgrade
		if (planData && planData.isPlanActive && planData.planPrice) {
			// Get current price without the $ sign, parse as number
			const currentPrice = parseFloat(planData.planPrice.replace('$', ''));
			
			// Check if this is a downgrade
			if (price < currentPrice) {
				// Use the existing toast message for plan validation
				toast.error("You've already activated a plan. Please check your current plan details.");
				return;
			}
			
			// Show confirmation modal for upgrades
			setShowModal(true);
		} else {
			// For new subscriptions without an existing plan, proceed directly
			handleSubscribe();
		}
	};
	
	const handleModalConfirm = () => {
		setShowModal(false);
		handleSubscribe();
	};
	
	const handleSubscribe = async () => {
		if (priceId == '1') {
			try {
				const response = await freeTrialTrigger(priceId);
				toast.success(response);
				window.location.href = appPages.subscriptionPage.to;

			} catch (err: any) {
				toast.error(err?.data?.msg || err.message);
			}
		} else {
			try {
				const paymentLink = await trigger(priceId);
				if (paymentLink) {
					window.location.href = paymentLink;
				} else {
					toast.error('Failed to generate payment link or no link returned');
				}
			} catch (err: any) {
				console.error('Error during payment link generation:', err);
				if (err.status == 400) {
				}
				toast.error(err?.data?.msg || err.message);
			}
		}
	};
	
	// Extract features from HTML description
	const getFeatures = () => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(description, 'text/html');
		const elements = doc.querySelectorAll('.planDescription');
		return Array.from(elements).map(el => el.textContent);
	};

	return (
		<>
			<motion.div
				className={`rounded-2xl border ${selectedPrice ? 'border-green-400 border-2' : 'border-gray-200'} bg-white shadow-lg overflow-hidden`}
				whileHover={{ scale: 1.03 }}
				transition={{ duration: 0.3 }}>
				{selectedPrice && (
					<div className="bg-green-600 text-white text-center py-1 font-medium">
						Current Plan
					</div>
				)}
				<div className="p-6 text-center">
					<h3 className='text-2xl font-bold text-gray-800 mb-2'>{title}</h3>
					<div className='flex items-center justify-center mb-4'>
						<span className='text-3xl font-semibold text-indigo-600'>CAD {price}</span>
						<span className='ml-2 text-gray-500'>/ {duration}</span>
					</div>
					
					<div className="border-t border-b border-gray-100 py-4 mb-4">
						<div dangerouslySetInnerHTML={{ __html: description }} className="text-left space-y-2" />
					</div>
					
					{showSubscribeButton && (
						<Button
							className={`w-full py-3 text-[16px] font-semibold ${
								selectedPrice
									? 'border-green-600 bg-green-600 !opacity-100'
									: 'primary-bg'
							}`}
							variant='solid'
							onClick={!selectedPrice ? handleSubscribeClick : undefined}
							isDisable={isLoading || belowPriceDisabled}
						>
							{selectedPrice ? 'Subscribed' : isLoading ? 'Processing...' : 'Subscribe'}
						</Button>
					)}
				</div>
			</motion.div>
			
			{/* Subscription Confirmation Modal */}
			<SubscriptionModal
				isOpen={showModal}
				onClose={() => setShowModal(false)}
				onConfirm={handleModalConfirm}
				currentPlan={planData?.planName?.toString() || 'No Plan'}
				newPlan={title}
				currentPrice={planData?.planPrice || '$0'}
				newPrice={`$${price}`}
				isPriceIncrease={isUpgrade()}
			/>
		</>
		
	);
};

export default SubscriptionProduct;
